import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save } from 'lucide-react'
import { parkingSpotApi } from '../services/api'
import api from '../services/api'
import { showError } from '../utils/showError'
import {
  DetailPageLayout,
  Card,
  CardHeader,
  CardBody,
  Button,
  FormField,
  FormSelect,
  FormCheckbox,
  ErrorMessage,
  SuccessMessage
} from '../components/common'

const BatchCreateParkingSpots = () => {
  const navigate = useNavigate()
  const { buildingId: routeBuildingId } = useParams()

  const [submitting, setSubmitting] = useState(false)
  const [buildings, setBuildings] = useState([])
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    buildingId: routeBuildingId || '',
    namePrefix: '',
    startNumber: 1,
    endNumber: 10,
    floor: '',
    isHandicapAccessible: false,
    isElectricCharging: false,
    isAvailable24_7: false,
    startTime: '',
    endTime: '',
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    loadBuildings()
  }, [])

  const loadBuildings = async () => {
    try {
      const response = await api.get('/Building', { params: { pageNumber: 1, pageSize: 200 } })
      setBuildings(response.data.items || [])
    } catch (err) {
      console.error('Error loading buildings:', err)
    }
  }

  const spotCount = Math.max(0, formData.endNumber - formData.startNumber + 1)

  const validateForm = () => {
    const newErrors = {}
    if (!formData.buildingId) newErrors.buildingId = 'Building is required'
    if (!formData.namePrefix.trim()) newErrors.namePrefix = 'Name prefix is required'
    if (formData.startNumber < 1) newErrors.startNumber = 'Start number must be at least 1'
    if (formData.endNumber < formData.startNumber) newErrors.endNumber = 'End number must be >= start number'
    if (spotCount > 50) newErrors.endNumber = 'Cannot create more than 50 spots at once'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }))
    setError('')
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    try {
      setSubmitting(true)
      setError('')
      await parkingSpotApi.batchCreate({
        buildingId: formData.buildingId,
        namePrefix: formData.namePrefix,
        startNumber: parseInt(formData.startNumber),
        endNumber: parseInt(formData.endNumber),
        floor: formData.floor || null,
        isHandicapAccessible: formData.isHandicapAccessible,
        isElectricCharging: formData.isElectricCharging,
        isAvailable24_7: formData.isAvailable24_7,
        startTime: formData.isAvailable24_7 ? null : (formData.startTime || null),
        endTime: formData.isAvailable24_7 ? null : (formData.endTime || null),
      })
      navigate('/parking')
    } catch (err) {
      setError(err.response?.data || 'Failed to batch create parking spots')
      console.error('Error batch creating:', err)
      showError(err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <DetailPageLayout
      title="Batch Create Parking Spots"
      backLink={{ label: 'Back to Parking', href: '/parking' }}
      actions={[
        <Button
          key="save"
          onClick={handleSubmit}
          disabled={submitting}
          variant="primary"
          icon={<Save className="h-4 w-4" />}
          loading={submitting}
        >
          {submitting ? 'Creating...' : `Create ${spotCount} Spots`}
        </Button>
      ]}
    >
      {error && <ErrorMessage message={error} />}

      <Card>
        <CardHeader title="Batch Details" subtitle={`Will create ${spotCount} parking spot(s)`} />
        <CardBody className="space-y-6">
          <FormSelect
            label="Building"
            value={formData.buildingId}
            onChange={(e) => handleInputChange('buildingId', e.target.value)}
            error={errors.buildingId}
            options={[
              { value: '', label: 'Select a building' },
              ...buildings.map(b => ({ value: b.id, label: b.name || b.userName }))
            ]}
            required
          />

          <FormField
            label="Name Prefix"
            value={formData.namePrefix}
            onChange={(e) => handleInputChange('namePrefix', e.target.value)}
            error={errors.namePrefix}
            placeholder="e.g., P1, B2, Visitor"
            helpText={formData.namePrefix ? `Spots will be named: ${formData.namePrefix}-${String(formData.startNumber).padStart(2, '0')} to ${formData.namePrefix}-${String(formData.endNumber).padStart(2, '0')}` : 'Enter a prefix for spot names'}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormField
              label="Start Number"
              type="number"
              value={formData.startNumber}
              onChange={(e) => handleInputChange('startNumber', parseInt(e.target.value) || 1)}
              error={errors.startNumber}
              required
            />
            <FormField
              label="End Number"
              type="number"
              value={formData.endNumber}
              onChange={(e) => handleInputChange('endNumber', parseInt(e.target.value) || 1)}
              error={errors.endNumber}
              required
            />
            <FormField
              label="Floor"
              value={formData.floor}
              onChange={(e) => handleInputChange('floor', e.target.value)}
              placeholder="e.g., P1, B2, Ground"
            />
          </div>

          <FormCheckbox
            label="Available 24/7"
            checked={formData.isAvailable24_7}
            onChange={(e) => handleInputChange('isAvailable24_7', e.target.checked)}
          />

          {!formData.isAvailable24_7 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                label="Start Time"
                type="time"
                value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
              />
              <FormField
                label="End Time"
                type="time"
                value={formData.endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
              />
            </div>
          )}

          <div className="space-y-3">
            <FormCheckbox
              label="Handicap Accessible"
              checked={formData.isHandicapAccessible}
              onChange={(e) => handleInputChange('isHandicapAccessible', e.target.checked)}
            />
            <FormCheckbox
              label="Electric Vehicle Charging"
              checked={formData.isElectricCharging}
              onChange={(e) => handleInputChange('isElectricCharging', e.target.checked)}
            />
          </div>
        </CardBody>
      </Card>
    </DetailPageLayout>
  )
}

export default BatchCreateParkingSpots
